import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { Loader2 } from "lucide-react";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { NewsletterForm } from "@/components/newsletter/NewsletterForm";

type ModeConfig = { enabled?: boolean; title?: string; content?: string };

export function GlobalModeOverlay() {
  const { isAdmin, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [maintenance, setMaintenance] = useState<ModeConfig>({});
  const [comingSoon, setComingSoon] = useState<ModeConfig>({});

  // Escludiamo TUTTE le pagine di autenticazione e sitemap dall'overlay
  const isAuthPage = location.pathname.startsWith("/auth/");
  const isSitemapPage = location.pathname.includes("sitemap");

  const activeMode = useMemo(() => {
    if (maintenance?.enabled) return { type: "maintenance", ...maintenance };
    if (comingSoon?.enabled) return { type: "comingSoon", ...comingSoon };
    return null;
  }, [maintenance, comingSoon]);

  const shouldOverlay = !isAuthPage && !isSitemapPage && !isLoading && !isAdmin() && !!activeMode;

  // Debug logging
  useEffect(() => {
    console.log("🔍 GlobalModeOverlay Debug:", {
      isAuthPage,
      isLoading,
      isAdmin: isAdmin(),
      isAuthenticated,
      maintenance,
      comingSoon,
      activeMode,
      shouldOverlay
    });
  }, [isAuthPage, isLoading, isAuthenticated, maintenance, comingSoon, activeMode, shouldOverlay, isAdmin]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [m, c] = await Promise.all([
          supabase.from("app_settings").select("value").eq("key", "maintenance_mode").maybeSingle(),
          supabase.from("app_settings").select("value").eq("key", "coming_soon_mode").maybeSingle(),
        ]);
        if (!mounted) return;
        setMaintenance((m.data?.value as ModeConfig) || {});
        setComingSoon((c.data?.value as ModeConfig) || {});
      } catch (error) {
        console.error("Error loading mode settings:", error);
      } finally {
        if (mounted) setChecking(false);
      }
    };
    load();

    // Realtime subscription for live updates
    const channel = supabase
      .channel("app_settings_modes")
      .on(
        "postgres_changes",
        { 
          event: "*", 
          schema: "public", 
          table: "app_settings", 
          filter: "key=in.(maintenance_mode,coming_soon_mode)" 
        },
        (payload: any) => {
          const newData = payload.new as { key?: string; value?: ModeConfig } | null;
          if (!newData?.key || !newData?.value) return;
          if (newData.key === "maintenance_mode") setMaintenance(newData.value);
          if (newData.key === "coming_soon_mode") setComingSoon(newData.value);
        }
      )
      .subscribe((status, err) => {
        // Silently handle subscription errors to prevent console noise during Lighthouse scans
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed silently - realtime updates will not work but app functions normally
        }
      });

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  // Scroll blocking removed - overlay itself is now scrollable

  // Never show overlay on auth pages or sitemap pages
  if (isAuthPage || isSitemapPage) return null;

  // Show loading state to prevent flicker
  if (checking || isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // No overlay needed
  if (!shouldOverlay) return null;

  const content = activeMode?.content || "Stiamo lavorando per offrirti la migliore esperienza possibile.";

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm overflow-auto">
      <div className="w-full min-h-full bg-background text-foreground p-4 sm:p-6 md:p-10 flex flex-col items-center py-8 md:py-12">
        <div
          className="prose prose-sm md:prose-base dark:prose-invert max-w-none text-center mb-4 sm:mb-6"
          dangerouslySetInnerHTML={{ 
            __html: sanitizeHtml(content, { 
              allowedTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'br', 'div', 'span'],
              allowedAttributes: ['href', 'src', 'alt', 'class', 'target', 'rel']
            }) 
          }}
        />
        {/* Newsletter/Waitlist Form - solo per Coming Soon */}
        {activeMode?.type === "comingSoon" && (
          <div className="w-full max-w-4xl mt-4 sm:mt-8 px-2 sm:px-4">
            <NewsletterForm variant="full" listType="waitlist" colorScheme="light" />
          </div>
        )}
        {!isAdmin() && !isAuthenticated && (
          <div className="mt-4 sm:mt-8 flex justify-center pb-8">
            <BrutalButton onClick={() => navigate("/auth/login")}>
              Vai al login
            </BrutalButton>
          </div>
        )}
      </div>
    </div>
  );
}
